import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Play, Eye, Trash2, RefreshCw, ArrowLeft } from "lucide-react";
import { SCField, SCModal, SCSection, SCEmpty, useConfirm, fmtDate } from "./Common";
import {
    scAdminListRankings, scAdminCreateRanking, scAdminPatchRanking, scAdminDeleteRanking,
    scAdminComputeRanking, scAdminRankingResults, scAdminMetricsCatalog, scAdminRecomputeAnalytics,
} from "../../../lib/sifcontrolApi";

const RULES = [
    { value: "REWEIGHT", label: "Reweight (redistribute weight of missing metrics)" },
    { value: "EXCLUDE", label: "Exclude (drop SIF if any metric missing)" },
    { value: "NEUTRAL", label: "Neutral (missing = 50 score)" },
];

const emptyMethodology = () => ({
    name: "New Ranking", description: "", status: "active",
    universe: { strategy_categories: [], min_fund_age_days: 90, min_aum_cr: 0 },
    min_metrics_required: 3, missing_data_rule: "REWEIGHT", normalization: "PERCENTILE",
    parameters: [
        { metric_name: "cagr_3y", weight: 30, direction: "HIGHER_IS_BETTER" },
        { metric_name: "sharpe", weight: 25, direction: "HIGHER_IS_BETTER" },
        { metric_name: "max_drawdown", weight: 25, direction: "LOWER_IS_BETTER" },
        { metric_name: "rolling_1y_positive_pct", weight: 20, direction: "HIGHER_IS_BETTER" },
    ],
});

const WeightBadge = ({ total }) => {
    const ok = Math.abs(total - 100) < 0.01;
    return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`} data-testid="sc-rank-weight-total">
            Weights total: {total.toFixed(2)}%
        </span>
    );
};

const RankingResults = ({ ranking, onBack }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => { const r = await scAdminRankingResults(ranking.id); setData(r); };
    useEffect(() => { load().catch(() => toast.error("Could not load results")); /* eslint-disable-next-line */ }, [ranking.id]);
    const compute = async () => {
        setBusy(true);
        try {
            const s = await scAdminComputeRanking(ranking.id);
            toast.success(`Computed: ${s.ranked}/${s.universe_size} ranked, ${s.unranked} unranked.`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Compute failed"); }
        finally { setBusy(false); }
    };
    if (!data) return <div className="p-8 text-sm font-body text-oth-ink3">Loading…</div>;
    const ranked = (data.results || []).filter((r) => r.status === "ranked");
    const unranked = (data.results || []).filter((r) => r.status === "unranked");
    return (
        <div data-testid="sc-rank-results">
            <div className="flex items-center gap-3 mb-4">
                <button onClick={onBack} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-rank-results-back"><ArrowLeft size={12} /> Back</button>
                <div className="flex-1">
                    <div className="font-display text-xl font-semibold tracking-tight">{ranking.name} · Results</div>
                    <div className="text-[11px] font-body text-oth-ink3">Version {ranking.version} · Last calculated: {ranking.last_calculated_at ? fmtDate(ranking.last_calculated_at) : "Never"}</div>
                </div>
                <button onClick={compute} disabled={busy} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-rank-recompute"><Play size={12} /> {busy ? "Computing…" : "Compute now"}</button>
            </div>
            {ranked.length === 0 && unranked.length === 0 ? (
                <SCEmpty message="No results yet. Click 'Compute now' after analytics have run." testId="sc-rank-empty" />
            ) : (
                <>
                    {ranked.length > 0 && (
                        <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden mb-4">
                            <div className="grid grid-cols-[50px_1fr_140px_100px_90px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                                <div>Rank</div><div>SIF</div><div>Strategy</div><div>Score</div><div className="text-right">Stars</div>
                            </div>
                            {ranked.map((r) => (
                                <div key={r.sif_id} className="grid grid-cols-[50px_1fr_140px_100px_90px] items-center px-5 py-3 border-b border-oth-line/60 last:border-b-0" data-testid={`sc-rank-row-${r.sif_id}`}>
                                    <div className="text-sm font-body text-oth-ink2">#{r.rank}</div>
                                    <div className="font-display text-sm font-medium text-oth-ink truncate">{r.sif_name}<div className="text-[11px] font-body text-oth-ink3">{r.sif_code}</div></div>
                                    <div className="text-xs font-body text-oth-ink2 truncate">{r.strategy_category || "—"}</div>
                                    <div className="text-sm font-body text-oth-ink">{r.score?.toFixed(1)}</div>
                                    <div className="text-right text-sm font-body text-oth-ink">{r.star_label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {unranked.length > 0 && (
                        <div className="rounded-2xl border border-oth-line/70 bg-oth-bg-2/40 overflow-hidden">
                            <div className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body border-b border-oth-line/60">Unranked ({unranked.length})</div>
                            {unranked.map((r) => (
                                <div key={r.sif_id} className="grid grid-cols-[1fr_1fr] items-center px-5 py-2.5 border-b border-oth-line/40 last:border-b-0 text-xs font-body">
                                    <div><span className="font-medium text-oth-ink">{r.sif_name}</span> <span className="text-oth-ink3">· {r.sif_code}</span></div>
                                    <div className="text-oth-ink3">{r.unranked_reason}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default function RankingsTab() {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [recomputing, setRecomputing] = useState(false);
    const { confirm, dialog } = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const [r, c] = await Promise.all([scAdminListRankings(), scAdminMetricsCatalog()]);
            setItems(r.items || []);
            setCatalog(c.metrics || []);
        } catch { toast.error("Could not load rankings"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const catalogByName = useMemo(() => Object.fromEntries(catalog.map((m) => [m.metric_name, m])), [catalog]);

    const openCreate = () => setModal({ mode: "create", data: emptyMethodology() });
    const openEdit = (row) => setModal({ mode: "edit", data: { ...row } });

    const setF = (k, v) => setModal({ ...modal, data: { ...modal.data, [k]: v } });
    const setUniverse = (k, v) => setModal({ ...modal, data: { ...modal.data, universe: { ...(modal.data.universe || {}), [k]: v } } });
    const setParam = (idx, k, v) => setModal({ ...modal, data: { ...modal.data, parameters: modal.data.parameters.map((p, i) => i === idx ? { ...p, [k]: v } : p) } });
    const addParam = () => setModal({ ...modal, data: { ...modal.data, parameters: [...modal.data.parameters, { metric_name: catalog[0]?.metric_name || "sharpe", weight: 0, direction: catalog[0]?.direction || "HIGHER_IS_BETTER" }] } });
    const removeParam = (idx) => setModal({ ...modal, data: { ...modal.data, parameters: modal.data.parameters.filter((_, i) => i !== idx) } });

    const weightTotal = modal ? modal.data.parameters.reduce((s, p) => s + (Number(p.weight) || 0), 0) : 0;

    const save = async () => {
        const d = modal.data;
        if (Math.abs(weightTotal - 100) > 0.01) return toast.error(`Weights must sum to 100 (currently ${weightTotal.toFixed(2)})`);
        setSaving(true);
        try {
            if (modal.mode === "create") await scAdminCreateRanking(d);
            else {
                const { id, created_at, updated_at, last_calculated_at, version, last_universe_size, last_ranked_count, ...rest } = d; // eslint-disable-line no-unused-vars
                await scAdminPatchRanking(id, rest);
            }
            toast.success("Saved");
            setModal(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const del = async (row) => {
        if (!(await confirm(`Delete "${row.name}"? All snapshots will be lost.`))) return;
        try { await scAdminDeleteRanking(row.id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const compute = async (row) => {
        try {
            const s = await scAdminComputeRanking(row.id);
            toast.success(`Ranked ${s.ranked}/${s.universe_size} · ${s.unranked} unranked`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Compute failed"); }
    };

    const recomputeAll = async () => {
        setRecomputing(true);
        try {
            const r = await scAdminRecomputeAnalytics();
            toast.success(`Analytics recomputed for ${r.analytics?.sifs_processed || 0} SIFs`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Recompute failed"); }
        finally { setRecomputing(false); }
    };

    if (viewing) return <RankingResults ranking={viewing} onBack={() => { setViewing(null); load(); }} />;

    return (
        <div data-testid="sc-admin-rankings">
            <SCSection
                title="Rankings"
                subtitle="Percentile-normalised methodologies. Weights must sum to 100%. Missing metrics use the configured rule (default: reweight)."
                right={
                    <div className="flex items-center gap-2">
                        <button onClick={recomputeAll} disabled={recomputing} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-rank-recompute-all">
                            {recomputing ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />} Recompute analytics
                        </button>
                        <button onClick={openCreate} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-rank-add-btn"><Plus size={14} /> New ranking</button>
                    </div>
                }
                testId="sc-rankings-header"
            />

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <SCEmpty message="No rankings yet." testId="sc-rankings-empty" />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[1.4fr_100px_100px_120px_160px_120px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>Ranking</div><div>Version</div><div>Params</div><div>Last calc</div><div>Coverage</div><div className="text-right">Actions</div>
                    </div>
                    <div className="divide-y divide-oth-line/60">
                        {items.map((r) => (
                            <div key={r.id} className="grid grid-cols-[1.4fr_100px_100px_120px_160px_120px] items-center px-5 py-3.5" data-testid={`sc-rank-row-${r.id}`}>
                                <div>
                                    <div className="font-display text-sm font-medium text-oth-ink">{r.name}</div>
                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5 truncate">{r.description}</div>
                                </div>
                                <div className="text-xs font-body text-oth-ink2">v{r.version}</div>
                                <div className="text-xs font-body text-oth-ink2">{r.parameters?.length ?? 0}</div>
                                <div className="text-[11px] font-body text-oth-ink3">{r.last_calculated_at ? fmtDate(r.last_calculated_at) : "Never"}</div>
                                <div className="text-[11px] font-body text-oth-ink3">{r.last_ranked_count || 0} of {r.last_universe_size || 0}</div>
                                <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => compute(r)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" aria-label="Compute" data-testid={`sc-rank-compute-${r.id}`}><Play size={13} /></button>
                                    <button onClick={() => setViewing(r)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" aria-label="View results" data-testid={`sc-rank-view-${r.id}`}><Eye size={13} /></button>
                                    <button onClick={() => openEdit(r)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" aria-label="Edit" data-testid={`sc-rank-edit-${r.id}`}>✎</button>
                                    <button onClick={() => del(r)} className="w-8 h-8 rounded-full hover:bg-red-50 text-oth-ink3 hover:text-red-700 flex items-center justify-center" aria-label="Delete" data-testid={`sc-rank-delete-${r.id}`}><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <SCModal
                open={!!modal}
                title={modal?.mode === "create" ? "New ranking methodology" : "Edit ranking methodology"}
                onClose={() => !saving && setModal(null)}
                testId="sc-rank-modal"
                maxWidth={780}
                footer={
                    <div className="flex items-center gap-3 w-full justify-between">
                        <WeightBadge total={weightTotal} />
                        <div className="flex items-center gap-3">
                            <button onClick={() => setModal(null)} disabled={saving} className="btn-pill !text-sm" data-testid="sc-rank-modal-cancel">Cancel</button>
                            <button onClick={save} disabled={saving || Math.abs(weightTotal - 100) > 0.01} className="btn-pill btn-pill-dark !text-sm" data-testid="sc-rank-modal-save">{saving ? "Saving…" : "Save"}</button>
                        </div>
                    </div>
                }
            >
                {modal && (
                    <div>
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Name" value={modal.data.name} onChange={(v) => setF("name", v)} testId="sc-rank-form-name" required />
                            <SCField label="Status" value={modal.data.status} onChange={(v) => setF("status", v)} type="select" options={["active", "inactive"]} testId="sc-rank-form-status" />
                        </div>
                        <SCField label="Description" value={modal.data.description} onChange={(v) => setF("description", v)} type="textarea" testId="sc-rank-form-desc" />
                        <div className="grid grid-cols-3 gap-3">
                            <SCField label="Min fund age (days)" value={modal.data.universe?.min_fund_age_days} onChange={(v) => setUniverse("min_fund_age_days", Number(v))} type="number" testId="sc-rank-form-age" />
                            <SCField label="Min AUM (₹ Cr)" value={modal.data.universe?.min_aum_cr} onChange={(v) => setUniverse("min_aum_cr", Number(v))} type="number" testId="sc-rank-form-aum" />
                            <SCField label="Min metrics required" value={modal.data.min_metrics_required} onChange={(v) => setF("min_metrics_required", Number(v))} type="number" testId="sc-rank-form-minreq" />
                        </div>
                        <SCField label="Missing-data rule" value={modal.data.missing_data_rule} onChange={(v) => setF("missing_data_rule", v)} type="select" options={RULES} testId="sc-rank-form-rule" />

                        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mt-4 mb-2">Parameters</div>
                        <div className="rounded-xl border border-oth-line/70 overflow-hidden">
                            <div className="grid grid-cols-[1.5fr_120px_130px_40px] text-[10px] uppercase tracking-[0.14em] text-oth-ink3 font-body px-3 py-2 bg-oth-bg-2/60">
                                <div>Metric</div><div>Weight %</div><div>Direction</div><div />
                            </div>
                            {modal.data.parameters.map((p, i) => {
                                const catMeta = catalogByName[p.metric_name];
                                return (
                                    <div key={i} className="grid grid-cols-[1.5fr_120px_130px_40px] items-center gap-2 px-3 py-2 border-t border-oth-line/50" data-testid={`sc-rank-param-${i}`}>
                                        <select value={p.metric_name} onChange={(e) => setParam(i, "metric_name", e.target.value)} className="h-9 px-2 rounded-lg border border-oth-line/70 bg-white text-xs" data-testid={`sc-rank-param-metric-${i}`}>
                                            {catalog.map((m) => <option key={m.metric_name} value={m.metric_name}>{m.label}</option>)}
                                        </select>
                                        <input type="number" step="0.01" value={p.weight} onChange={(e) => setParam(i, "weight", Number(e.target.value))} className="h-9 px-2 rounded-lg border border-oth-line/70 bg-white text-xs" data-testid={`sc-rank-param-weight-${i}`} />
                                        <select value={p.direction} onChange={(e) => setParam(i, "direction", e.target.value)} className="h-9 px-2 rounded-lg border border-oth-line/70 bg-white text-xs" data-testid={`sc-rank-param-direction-${i}`}>
                                            <option value="HIGHER_IS_BETTER">Higher is better</option>
                                            <option value="LOWER_IS_BETTER">Lower is better</option>
                                        </select>
                                        <button onClick={() => removeParam(i)} className="w-8 h-8 rounded-full hover:bg-red-50 text-oth-ink3 hover:text-red-700 flex items-center justify-center" data-testid={`sc-rank-param-remove-${i}`}><Trash2 size={13} /></button>
                                        {catMeta && catMeta.direction !== p.direction && (
                                            <div className="col-span-4 text-[10px] font-body text-yellow-700 -mt-1">Direction differs from catalog default ({catMeta.direction === "LOWER_IS_BETTER" ? "lower" : "higher"}).</div>
                                        )}
                                    </div>
                                );
                            })}
                            <button onClick={addParam} className="w-full py-2.5 text-xs font-body text-oth-ink2 hover:bg-oth-bg-2/60 border-t border-oth-line/60" data-testid="sc-rank-param-add">+ Add parameter</button>
                        </div>
                        <div className="mt-2 text-[11px] font-body text-oth-ink3">
                            Editing weights, parameters, missing-data rule or universe bumps the ranking version. Prior snapshots are preserved.
                        </div>
                    </div>
                )}
            </SCModal>
            {dialog}
        </div>
    );
}
